/**
 * Nigerian reference data for KYC and bank details.
 *
 * Held here rather than in a database table because these change rarely, and a
 * member filling in a form should never be blocked by a lookup table nobody
 * remembered to seed.
 */
export const NIGERIAN_STATES = [
    'Abia',
    'Adamawa',
    'Akwa Ibom',
    'Anambra',
    'Bauchi',
    'Bayelsa',
    'Benue',
    'Borno',
    'Cross River',
    'Delta',
    'Ebonyi',
    'Edo',
    'Ekiti',
    'Enugu',
    'Federal Capital Territory',
    'Gombe',
    'Imo',
    'Jigawa',
    'Kaduna',
    'Kano',
    'Katsina',
    'Kebbi',
    'Kogi',
    'Kwara',
    'Lagos',
    'Nasarawa',
    'Niger',
    'Ogun',
    'Ondo',
    'Osun',
    'Oyo',
    'Plateau',
    'Rivers',
    'Sokoto',
    'Taraba',
    'Yobe',
    'Zamfara',
];
/**
 * Commercial banks, with their NIBSS codes.
 *
 * Not exhaustive — members can still type a bank not listed here, because a
 * missing entry must never stop someone receiving a loan.
 */
export const NIGERIAN_BANKS = [
    { name: 'Access Bank', code: '044' },
    { name: 'Citibank Nigeria', code: '023' },
    { name: 'Ecobank Nigeria', code: '050' },
    { name: 'Fidelity Bank', code: '070' },
    { name: 'First Bank of Nigeria', code: '011' },
    { name: 'First City Monument Bank', code: '214' },
    { name: 'Globus Bank', code: '00103' },
    { name: 'Guaranty Trust Bank', code: '058' },
    { name: 'Heritage Bank', code: '030' },
    { name: 'Jaiz Bank', code: '301' },
    { name: 'Keystone Bank', code: '082' },
    { name: 'Kuda Bank', code: '50211' },
    { name: 'Lotus Bank', code: '303' },
    { name: 'Moniepoint MFB', code: '50515' },
    { name: 'Opay', code: '999992' },
    { name: 'Palmpay', code: '999991' },
    { name: 'Parallex Bank', code: '104' },
    { name: 'Polaris Bank', code: '076' },
    { name: 'Providus Bank', code: '101' },
    { name: 'Stanbic IBTC Bank', code: '221' },
    { name: 'Standard Chartered Bank', code: '068' },
    { name: 'Sterling Bank', code: '232' },
    { name: 'SunTrust Bank', code: '100' },
    { name: 'TAJBank', code: '302' },
    { name: 'Titan Trust Bank', code: '102' },
    { name: 'Union Bank of Nigeria', code: '032' },
    { name: 'United Bank For Africa', code: '033' },
    { name: 'Unity Bank', code: '215' },
    { name: 'Wema Bank', code: '035' },
    { name: 'Zenith Bank', code: '057' },
];
/** Non-interest banks, listed first for a membership that may prefer them. */
export const ISLAMIC_BANKS = ['Jaiz Bank', 'TAJBank', 'Lotus Bank'];
export const IDENTIFICATION_TYPES = [
    { value: 'NIN', label: 'National Identification Number (NIN)' },
    { value: 'INTERNATIONAL_PASSPORT', label: 'International passport' },
    { value: 'DRIVERS_LICENCE', label: "Driver's licence" },
    { value: 'VOTERS_CARD', label: "Voter's card" },
];
export const EMPLOYMENT_STATUS_LABELS = {
    EMPLOYED: 'Employed',
    SELF_EMPLOYED: 'Self-employed',
    UNEMPLOYED: 'Unemployed',
    STUDENT: 'Student',
    RETIRED: 'Retired',
    OTHER: 'Other',
};
export const DOCUMENT_TYPE_LABELS = {
    GOVERNMENT_ID: 'Government-issued ID',
    PASSPORT_PHOTOGRAPH: 'Passport photograph',
    PROOF_OF_ADDRESS: 'Proof of address',
    EMPLOYMENT_DOCUMENT: 'Employment document',
    MEMBERSHIP_EVIDENCE: 'Evidence of membership',
    BANK_STATEMENT: 'Bank statement',
    SIGNATURE: 'Signature',
    OTHER: 'Other document',
};
/** Broad income bands, so nobody has to disclose an exact salary. */
export const INCOME_BANDS = [
    'Below ₦50,000',
    '₦50,000 – ₦150,000',
    '₦150,000 – ₦300,000',
    '₦300,000 – ₦500,000',
    '₦500,000 – ₦1,000,000',
    'Above ₦1,000,000',
    'Prefer not to say',
];
/**
 * Format of each Nigerian identification number.
 *
 * Checked because an identification number is the one field a verification
 * officer cannot correct from context: a transposed NIN looks exactly like a
 * valid one, and the error only surfaces when someone tries to verify it
 * against the national register.
 *
 * These are shape checks, not proof the number exists — that needs NIMC or the
 * issuing authority. They catch typos and the wrong document being entered
 * under the wrong type, which is the common case.
 */
export const IDENTIFICATION_FORMATS = {
    // NIMC issues exactly 11 digits.
    NIN: {
        pattern: /^\d{11}$/,
        hint: 'A NIN is 11 digits.',
        normalise: (v) => v.replace(/\D/g, ''),
    },
    // One letter followed by eight digits, e.g. A01234567.
    INTERNATIONAL_PASSPORT: {
        pattern: /^[A-Z]\d{8}$/,
        hint: 'A passport number is a letter followed by 8 digits, e.g. A01234567.',
        normalise: (v) => v.toUpperCase().replace(/[^A-Z0-9]/g, ''),
    },
    // FRSC format: three letters, five digits, two letters, two digits.
    DRIVERS_LICENCE: {
        pattern: /^[A-Z]{3}\d{5}[A-Z]{2}\d{2}$/,
        hint: "A driver's licence is like ABC12345DE67.",
        normalise: (v) => v.toUpperCase().replace(/[^A-Z0-9]/g, ''),
    },
    // INEC voter identification number: 19 alphanumeric characters.
    VOTERS_CARD: {
        pattern: /^[A-Z0-9]{19}$/,
        hint: "A voter's card number is 19 letters and digits.",
        normalise: (v) => v.toUpperCase().replace(/[^A-Z0-9]/g, ''),
    },
};
/** Strips formatting a member may have typed, per identification type. */
export function normaliseIdentificationNumber(type, value) {
    const format = IDENTIFICATION_FORMATS[type];
    const trimmed = value.trim();
    return format?.normalise ? format.normalise(trimmed) : trimmed;
}
/** Returns the problem with an identification number, or null when it is fine. */
export function validateIdentificationNumber(type, value) {
    const format = IDENTIFICATION_FORMATS[type];
    if (!format)
        return null;
    return format.pattern.test(normaliseIdentificationNumber(type, value)) ? null : format.hint;
}
/**
 * Live checks for the two banking numbers a member types.
 *
 * Both return null while the field is empty: an untouched field is incomplete,
 * not wrong. They mirror accountNumberSchema and bvnSchema exactly, so the
 * browser and the API never disagree about what is acceptable.
 */
export function validateAccountNumber(value) {
    const digits = value.trim();
    if (!digits)
        return null;
    return /^\d{10}$/.test(digits) ? null : `Account numbers are 10 digits. You have ${digits.length}.`;
}
export function validateBvn(value) {
    const digits = value.trim();
    if (!digits)
        return null;
    return /^\d{11}$/.test(digits) ? null : `A BVN is 11 digits. You have ${digits.length}.`;
}
//# sourceMappingURL=nigeria.js.map